/**
 * HITLValidationLayout Component
 * Main orchestration component for the HITL Validation Interface
 * Requirements: 1.1, 1.3, 6.4
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import PanToolOutlinedIcon from '@mui/icons-material/PanToolOutlined';

import { PDFViewerPanel, ValidationPanel } from '../';
import LensRecordPanel from '../validation/LensRecordPanel';
import { HITLContextProviders, useValidation } from '../../contexts';
import { ExtractedDocumentData } from '../../types';
import { ErrorBoundary } from '../error';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import StatusBadge from '../shared/StatusBadge';
import { mapSearchStatus } from '../../utils/searchResultColumns';
import { authFetch } from '../../services/authFetch';
import { useVisibilityPolling } from '../../hooks/useVisibilityPolling';
import type { WorkspaceDocumentRecord } from '../../hooks/useWorkspaceDocument';
import type { AnalyzerConfiguration } from '../../services/analyzerService';

/** Live lock/hold state for the open document (subset of the queue item shape). */
interface ReviewStatus {
  status: string;
  lockActive: boolean;
  lockedByMe: boolean;
  lockedByName?: string;
  onHold: boolean;
  holdAssignedToMe: boolean;
  heldByMe: boolean;
  heldByName?: string;
  lockTakeoverRequests: { requestedBy: string; requestedByName?: string; requestedAt: string }[];
}

const REVIEW_STATUS_POLL_MS = 8_000;

/**
 * Which workspace this shell is rendering (Lens S5).
 *
 * `'review'` is the HITL workspace: it claims and releases the review lock, polls for take-overs,
 * and renders the editable validation stages.
 *
 * `'lens'` is the read-only archive view reached from a search result. It performs NO writes of
 * any kind — no claim, no release, no status poll — and renders `LensRecordPanel` instead of the
 * editing tree, so there is no input to disable and no submit handler mounted.
 *
 * Deliberately NOT a bare `mode` prop: `mode` is already an `OutputStep` property
 * (`'api' | 'batch'`), and two different objects carrying the same property name is the mix-up
 * TypeScript cannot catch (lessons.md, 2026-08-12).
 */
export type WorkspaceMode = 'review' | 'lens';

interface HITLValidationLayoutProps {
  documentId: string;
  extractedData: ExtractedDocumentData;
  onBack?: () => void;
  onNextDocument?: (nextDocumentId: string) => void;
  /** When opened from the exception dashboard, restricts next-document navigation to the same group. */
  sourceGroup?: 'stranded' | 'failed' | 'onHold';
  /** Defaults to `'review'` so every existing caller keeps its current behaviour exactly. */
  workspaceMode?: WorkspaceMode;
  /** Lens only — the stored record, supplying correction history to the read-only panel. */
  record?: WorkspaceDocumentRecord | null;
  /**
   * Lens only — this document type's analyzer config, for display names and field provenance.
   * `undefined` means "not resolved yet"; see LensRecordPanel for the tri-state.
   */
  analyzerConfig?: AnalyzerConfiguration | null;
}

/**
 * Main layout component that orchestrates the two-column HITL interface
 * Provides responsive layout with PDF viewer on left and validation panel on right
 */
function HITLValidationLayoutContent({
  documentId,
  extractedData,
  onBack,
  onNextDocument,
  sourceGroup,
  workspaceMode = 'review',
  record = null,
  analyzerConfig,
}: HITLValidationLayoutProps) {
  const isLens = workspaceMode === 'lens';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  // Calculate responsive column proportions
  const pdfColumnSize = isMobile ? 12 : isTablet ? 6 : 6;
  const validationColumnSize = isMobile ? 12 : isTablet ? 6 : 6;

  // Keyboard shortcuts live with the stages that own them (UX4): field traversal, Enter-confirm,
  // Ctrl+Enter submit and the `?` overlay in DataValidationStage; Ctrl+. in SuccessStage.

  // Reservation-on-back (soft-lock cleanup). Opening the workspace claims (reserves) the document.
  // On Back we decide what to do with that reservation:
  //   • No progress yet (nothing edited or confirmed) → release the reservation immediately so the
  //     document drops straight back to "Ready to review" for everyone.
  //   • Progress made → ask whether to release it or keep it reserved so the analyst can finish later.
  const { state } = useValidation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const hasProgress = state.isDirty || Object.values(state.reviewedFields).some(Boolean);

  // Never leave a claimed document "in limbo". Opening the workspace claims (locks) the document;
  // we release that claim whenever the analyst leaves at the initial stage — by ANY exit path:
  // the Back button, an in-app navigation (sidebar/browser back), moving to the next document, or
  // a hard unload (tab close, refresh, session sign-out). The one exception is an explicit
  // "Keep reserved" choice after the analyst has started editing. The release endpoint is a
  // server-side no-op once the document is finalized or taken over, so firing it is always safe.
  const keepReservedRef = useRef(false); // analyst chose to hold the doc for later
  const releasedRef = useRef(false);     // guard so we release at most once per exit

  const releaseAndBack = useCallback(async () => {
    releasedRef.current = true; // explicit release — suppress the exit-cleanup double fire
    setReleasing(true);
    try {
      await authFetch('/queue/release', {
        method: 'POST',
        body: JSON.stringify({ documentId }),
      });
    } catch {
      // Best-effort release — navigate back regardless; an unreleased lock expires server-side.
    } finally {
      setReleasing(false);
      setConfirmOpen(false);
      onBack?.();
    }
  }, [documentId, onBack]);

  // Exit-cleanup release: covers every path the Back button doesn't (sidebar/browser navigation,
  // next-document, tab close, refresh, sign-out). Keyed on documentId so navigating between
  // documents releases the one we're leaving, then starts the next with a clean slate.
  useEffect(() => {
    // Lens never claimed anything, so it must never release anything. Returning before the
    // listener is even registered means the unmount cleanup cannot fire a `/queue/release` at
    // a document the reader only looked at — the single most likely way this shell would have
    // written to a document opened from search.
    if (isLens) return;

    keepReservedRef.current = false;
    releasedRef.current = false;

    const releaseOnExit = () => {
      if (keepReservedRef.current || releasedRef.current) return;
      releasedRef.current = true;
      // keepalive lets the request outlive an SPA unmount AND a real page unload.
      void authFetch('/queue/release', {
        method: 'POST',
        body: JSON.stringify({ documentId }),
        keepalive: true,
      }).catch(() => {
        /* best-effort; server lazily expires the lock as a backstop */
      });
    };

    window.addEventListener('pagehide', releaseOnExit);
    return () => {
      window.removeEventListener('pagehide', releaseOnExit);
      releaseOnExit();
    };
  }, [documentId, isLens]);

  const handleBack = useCallback(() => {
    // Lens holds no reservation to release or keep, so Back is plain navigation.
    if (isLens) {
      onBack?.();
      return;
    }
    if (hasProgress) {
      setConfirmOpen(true); // ask before dropping their in-progress reservation
    } else {
      releaseAndBack(); // first step, no work done → clear the reservation now
    }
  }, [isLens, onBack, hasProgress, releaseAndBack]);

  // ── One-editor-per-document: live lock watch (Area 4) ────────────────────────
  // Poll this document's lock/hold state while the workspace is open so we can (a) lock the
  // analyst out the instant a supervisor takes the document over or it's parked on hold, and
  // (b) prompt the current holder when someone asks to take it over. This is the enforcement
  // that guarantees only ONE person is ever actively editing a given document.
  const [reviewStatus, setReviewStatus] = useState<ReviewStatus | null>(null);
  const [granting, setGranting] = useState(false);
  const [leaving, setLeaving] = useState(false);
  // Requests the holder chose to keep working through — keyed so a NEW request still prompts.
  const dismissedReqsRef = useRef<Set<string>>(new Set());
  const [dismissedTick, setDismissedTick] = useState(0); // force re-eval after a dismissal

  const pollStatus = useCallback(async () => {
    // Lens is not an editing session, so there is no lock to watch and nobody to lock out. Not
    // polling also keeps a read from generating recurring background traffic per open document.
    if (isLens) return;
    try {
      const res = await authFetch(`/queue/review-status?documentId=${encodeURIComponent(documentId)}`);
      if (!res.ok) return; // transient — keep the last-known status
      const json = await res.json();
      if (json?.data) setReviewStatus(json.data as ReviewStatus);
    } catch {
      // Network hiccup — leave the last-known status in place rather than false-alarming.
    }
  }, [documentId, isLens]);

  useEffect(() => {
    setReviewStatus(null);
    dismissedReqsRef.current = new Set();
    pollStatus();
  }, [documentId, pollStatus]);

  // Disabled outright in lens mode — there is no lock to watch, so no timer and no visibility
  // listener are created at all (rather than waking every 8s to hit a callback that returns).
  useVisibilityPolling(pollStatus, REVIEW_STATUS_POLL_MS, !isLens);

  // The document was taken away from me: either someone else now holds an active lock, or it was
  // parked on hold by someone else. Either way I must not keep editing. (We deliberately do NOT
  // trip on finalized states — those are the normal submit/success flow this same analyst just
  // completed. We also do NOT evict on self-holds — the onHeld() callback navigates away; this
  // guard only fires for holds placed by others or addressed to others.)
  const lostReason: 'takeover' | 'hold' | null = reviewStatus
    ? reviewStatus.lockActive && !reviewStatus.lockedByMe
      ? 'takeover'
      : reviewStatus.onHold && !reviewStatus.holdAssignedToMe && !reviewStatus.heldByMe
        ? 'hold'
        : null
    : null;

  // I'm the holder and someone has asked to take this document over — surface the polite prompt.
  const pendingRequests = (reviewStatus?.lockedByMe && reviewStatus.lockTakeoverRequests) || [];
  const activeRequest = pendingRequests.find(
    (r) => !dismissedReqsRef.current.has(`${r.requestedBy}:${r.requestedAt}`)
  );
  void dismissedTick; // re-render dependency for the dismissed set

  const leaveAfterLoss = useCallback(() => {
    releasedRef.current = true; // we no longer hold the lock — suppress the exit-cleanup release
    setLeaving(true);
    onBack?.();
  }, [onBack]);

  const grantTo = useCallback(
    async (toUserId: string) => {
      setGranting(true);
      try {
        await authFetch('/queue/grant-takeover', {
          method: 'POST',
          body: JSON.stringify({ documentId, toUserId }),
        });
      } catch {
        // Best-effort — even on failure we hand control back; the server lock is authoritative.
      } finally {
        setGranting(false);
        releasedRef.current = true; // handed off — don't also release on exit
        onBack?.();
      }
    },
    [documentId, onBack]
  );

  const keepWorking = useCallback(() => {
    for (const r of pendingRequests) {
      dismissedReqsRef.current.add(`${r.requestedBy}:${r.requestedAt}`);
    }
    setDismissedTick((t) => t + 1);
  }, [pendingRequests]);

  const documentTitle = extractedData.metadata?.fileName || `Document ${documentId}`;

  const lensStatusBadge = record?.currentStatus ? mapSearchStatus(record.currentStatus) : undefined;

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* Back Navigation Header */}
      {onBack && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            flexShrink: 0, // Prevent shrinking
            zIndex: 10, // Ensure it's above other content
          }}
        >
          <IconButton
            onClick={handleBack}
            aria-label={isLens ? 'Back to search results' : 'Back to review queue'}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          {/* `component="h1"` keeps the visual scale but makes this the page's top heading —
              otherwise the document title rendered as a literal <h6> above the panel's <h2>,
              giving a screen-reader user the outline h6 → h2 → h3 with no h1 anywhere. */}
          <Typography
            variant="h6"
            component="h1"
            sx={{ fontWeight: 600 }}
            noWrap
            title={documentTitle}
          >
            {documentTitle}
          </Typography>
          {/* The document's REAL stored status, via the same mapper the search results use, so a
              reader sees the same word for the same document on both screens. An unrecognised
              status falls back to the raw stored value — which is `mapSearchStatus`'s own documented
              contract — rather than rendering nothing, so the status never silently disappears
              between the search row the reader arrived from and this header. */}
          {isLens && record?.currentStatus && (
            <Box sx={{ ml: 2 }}>
              {lensStatusBadge ? (
                <StatusBadge type="document-status" status={lensStatusBadge} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {record.currentStatus}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Release-vs-keep confirmation shown only when the analyst has started reviewing. */}
      <ConfirmDialog
        open={confirmOpen}
        titleId="release-hold-title"
        title="Keep this document reserved?"
        message={
          <>
            You've started reviewing <strong>{documentTitle}</strong>. Keep it reserved for you so you
            can finish later, or release it back to the queue for anyone to pick up.
          </>
        }
        busy={releasing}
        cancelLabel="Keep reserved"
        onCancel={() => {
          keepReservedRef.current = true; // hold the lock for later — suppress the exit-cleanup release
          setConfirmOpen(false);
          onBack?.();
        }}
        confirmLabel={releasing ? 'Releasing…' : 'Release document'}
        confirmColor="warning"
        onConfirm={releaseAndBack}
      />

      {/* Taken-over / on-hold lock-out. A blocking dialog that prevents any further editing or
          submission once this document is no longer mine — the enforcement of one editor at a time. */}
      <Dialog open={!!lostReason} aria-labelledby="review-lost-title">
        <DialogTitle id="review-lost-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockPersonIcon color="warning" aria-hidden="true" />
          {lostReason === 'hold' ? 'Document placed on hold' : 'Document taken over'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {lostReason === 'hold' ? (
              <>
                <strong>{documentTitle}</strong> has been put on hold
                {reviewStatus?.heldByName ? <> by <strong>{reviewStatus.heldByName}</strong></> : null} and is no
                longer assigned to you. Any changes you were making here were not saved.
              </>
            ) : (
              <>
                <strong>{reviewStatus?.lockedByName || 'Another reviewer'}</strong> has taken over{' '}
                <strong>{documentTitle}</strong>. To make sure only one person edits a document at a time, your
                copy is now read-only. Any unsaved changes were not kept.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={leaveAfterLoss} disabled={leaving} autoFocus>
            Back to review queue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Polite hand-off prompt: I hold the document and someone has asked to take it over. */}
      <Dialog open={!!activeRequest && !lostReason} aria-labelledby="takeover-request-title">
        <DialogTitle id="takeover-request-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PanToolOutlinedIcon color="warning" aria-hidden="true" />
          Take-over requested
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{activeRequest?.requestedByName || 'Another reviewer'}</strong>
            {pendingRequests.length > 1 ? <> (and {pendingRequests.length - 1} other{pendingRequests.length - 1 > 1 ? 's' : ''})</> : null}{' '}
            {pendingRequests.length > 1 ? 'want' : 'wants'} to take over <strong>{documentTitle}</strong>. Release it to
            them, or keep working — they'll keep waiting until you release it (a supervisor can force the hand-off).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={keepWorking} color="secondary" disabled={granting}>
            Keep working
          </Button>
          <Button
            variant="contained"
            color="warning"
            disableElevation
            onClick={() => activeRequest && grantTo(activeRequest.requestedBy)}
            disabled={granting}
          >
            {granting ? 'Releasing…' : `Release to ${activeRequest?.requestedByName || 'them'}`}
          </Button>
        </DialogActions>
      </Dialog>
      <Grid
        container
        sx={{
          flex: 1,
          height: 0, // Force flex child to respect parent height
          margin: 0,
          width: '100%',
          overflow: 'hidden',
        }}
        spacing={isMobile ? 1 : 2}
      >
        {/* PDF Viewer Panel */}
        <Grid
          item
          xs={pdfColumnSize}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            paddingLeft: theme.spacing(1),
            paddingRight: isMobile ? theme.spacing(1) : theme.spacing(1),
            overflow: 'hidden',
          }}
        >
          <ErrorBoundary
            resetKeys={[extractedData.documentUrl, documentId]}
            resetOnPropsChange={true}
          >
            <PDFViewerPanel
              documentUrl={extractedData.documentUrl}
              documentId={documentId}
              totalPages={extractedData.totalPages}
            />
          </ErrorBoundary>
        </Grid>

        {/* Validation Panel */}
        <Grid
          item
          xs={validationColumnSize}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            paddingLeft: isMobile ? theme.spacing(1) : theme.spacing(1),
            paddingRight: theme.spacing(1),
            borderLeft: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
            borderTop: isMobile ? `1px solid ${theme.palette.divider}` : 'none',
            overflow: 'hidden',
          }}
        >
          <ErrorBoundary
            resetKeys={[extractedData.documentId, extractedData.fields.length]}
            resetOnPropsChange={true}
          >
            {/* Lens mounts the read-only record instead of the editing tree. Not `ValidationPanel`
                with everything disabled: that would leave every input, Submit button and stage
                stepper mounted and one prop away from live. Here they are never constructed. */}
            {isLens ? (
              <LensRecordPanel
                extractedData={extractedData}
                record={record}
                analyzerConfig={analyzerConfig}
              />
            ) : (
              <ValidationPanel
                extractedData={extractedData}
                documentId={documentId}
                onNextDocument={onNextDocument}
                sourceGroup={sourceGroup}
              />
            )}
          </ErrorBoundary>
        </Grid>
      </Grid>
    </Box>
  );
}

/**
 * Main export component with context providers
 * Wraps the layout content with all necessary context providers
 */
export default function HITLValidationLayout(props: HITLValidationLayoutProps) {
  return (
    <HITLContextProviders>
      <HITLValidationLayoutContent {...props} />
    </HITLContextProviders>
  );
}